import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PayrollHeader from './components/PayrollHeader';
import QuickNavigation from './components/QuickNavigation';
import SearchFilterBar from './components/SearchFilterBar';
import PayrollList from './components/PayrollList';
import PayrollStats from './components/PayrollStats';
import QuickActions from './components/QuickActions';
import AddPayrollModal from './components/modals/AddPayrollModal';
import EditPayrollModal from './components/modals/EditPayrollModal';
import ProcessPayrollModal from './components/modals/ProcessPayrollModal';
import DeletePayrollModal from './components/modals/DeletePayrollModal';

const PayrollMain = () => {
    const [payrolls, setPayrolls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showProcessModal, setShowProcessModal] = useState(false);
    const [selectedPayroll, setSelectedPayroll] = useState(null);
    const [formData, setFormData] = useState({
        unit_id: '',
        month_year: '',
        from_date: '',
        to_date: '',
        status: 'Open'
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('month_year');
    const [statusFilter, setStatusFilter] = useState('');
    const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
    const [monthFilter, setMonthFilter] = useState('');

    // Additional data for dropdowns
    const [companies, setCompanies] = useState([]);
    const [units, setUnits] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [unitsLoading, setUnitsLoading] = useState(false);
    const [unitsError, setUnitsError] = useState('');

    // User permissions state
    const [userPermissions, setUserPermissions] = useState({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_process: false,
        can_export: false,
        loaded: false
    });

    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Fetch user permissions function
    const fetchUserPermissions = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_process: false,
                    can_export: false,
                    loaded: true
                });
                return;
            }

            const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.permissions) {
                // Find permissions for Payroll module
                const payrollPermissions = response.data.permissions.find(
                    p => p.module_name === 'Payroll'
                );

                if (payrollPermissions) {
                    setUserPermissions({
                        can_view: Boolean(payrollPermissions.can_view),
                        can_add: Boolean(payrollPermissions.can_add),
                        can_edit: Boolean(payrollPermissions.can_edit),
                        can_delete: Boolean(payrollPermissions.can_delete),
                        can_process: Boolean(payrollPermissions.can_process),
                        can_export: Boolean(payrollPermissions.can_export),
                        loaded: true
                    });
                } else {
                    setUserPermissions({
                        can_view: false,
                        can_add: false,
                        can_edit: false,
                        can_delete: false,
                        can_process: false,
                        can_export: false,
                        loaded: true
                    });
                }
            } else {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_process: false,
                    can_export: false,
                    loaded: true
                });
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setUserPermissions({
                can_view: false,
                can_add: false,
                can_edit: false,
                can_delete: false,
                can_process: false,
                can_export: false,
                loaded: true
            });
        }
    }, [API_BASE]);

    // Fetch units separately with better error handling
    const fetchUnits = useCallback(async () => {
        try {
            setUnitsLoading(true);
            setUnitsError('');
            const token = localStorage.getItem('authToken');
            if (!token) {
                setUnitsError('No authentication token found');
                setUnitsLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}/get_units.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setUnits(response.data.units || []);
            } else {
                setUnits([]);
                setUnitsError(response.data.error || 'Failed to fetch units');
            }
        } catch (error) {
            setUnits([]);
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to fetch units';
            setUnitsError(errorMsg);
            console.error('Error fetching units:', error);
        } finally {
            setUnitsLoading(false);
        }
    }, [API_BASE]);

    // Fetch other dropdown data
    const fetchOtherDropdownData = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            // Fetch companies (for display only, not for selection)
            const companiesRes = await axios.get(`${API_BASE}/get_companies.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (companiesRes.data.success) {
                setCompanies(companiesRes.data.companies || []);
            }

            // Fetch employees for processed_by dropdown
            const employeesRes = await axios.get(`${API_BASE}/get_employees.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (employeesRes.data.success) {
                setEmployees(employeesRes.data.employees || []);
            }
        } catch (error) {
            console.error('Error fetching other dropdown data:', error);
        }
    }, [API_BASE]);

    const fetchPayrolls = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}payroll/get_payrolls.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    year: yearFilter,
                    month: monthFilter,
                    status: statusFilter
                },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setPayrolls(response.data.payrolls || []);
                setError('');
            } else {
                setPayrolls([]);
                const errorMsg = response.data?.error || 'Failed to fetch payrolls';
                setError(errorMsg);
            }
        } catch (error) {
            setPayrolls([]);
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to fetch payrolls';
            setError(errorMsg);
            console.error('Error fetching payrolls:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, yearFilter, monthFilter, statusFilter]);

    useEffect(() => {
        const initializeData = async () => {
            await fetchUserPermissions();
            await fetchUnits();
            await fetchOtherDropdownData();
            await fetchPayrolls();
        };

        initializeData();
    }, [fetchUserPermissions, fetchUnits, fetchOtherDropdownData, fetchPayrolls]);

    // Permission check functions
    const canViewPayrolls = () => userPermissions.can_view;
    const canAddPayroll = () => userPermissions.can_add;
    const canEditPayroll = () => userPermissions.can_edit;
    const canDeletePayroll = () => userPermissions.can_delete;
    const canProcessPayroll = () => userPermissions.can_process;
    const canExportPayrolls = () => userPermissions.can_export;

    const handleAdd = async () => {
        if (!canAddPayroll()) {
            alert('You do not have permission to add payroll periods');
            return;
        }

        // Ensure units are loaded before showing modal
        if (units.length === 0 && !unitsLoading) {
            await fetchUnits();
        }

        const today = new Date().toISOString().split('T')[0];
        setFormData({
            unit_id: '',
            month_year: '',
            from_date: today,
            to_date: today,
            status: 'Open'
        });
        setValidationErrors({});
        setShowAddModal(true);
    };

    const handleEdit = async (payroll) => {
        if (!canEditPayroll()) {
            alert('You do not have permission to edit payroll periods');
            return;
        }

        // Ensure units are loaded before showing modal
        if (units.length === 0 && !unitsLoading) {
            await fetchUnits();
        }

        setSelectedPayroll(payroll);
        setFormData({
            unit_id: payroll.unit_id || '',
            month_year: payroll.month_year || '',
            from_date: payroll.from_date || '',
            to_date: payroll.to_date || '',
            status: payroll.status || 'Open'
        });
        setValidationErrors({});
        setShowEditModal(true);
    };

    const handleDelete = (payroll) => {
        if (!canDeletePayroll()) {
            alert('You do not have permission to delete payroll periods');
            return;
        }
        setSelectedPayroll(payroll);
        setShowDeleteModal(true);
    };

    const handleProcess = (payroll) => {
        if (!canProcessPayroll()) {
            alert('You do not have permission to process payroll');
            return;
        }
        setSelectedPayroll(payroll);
        setShowProcessModal(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.unit_id) {
            errors.unit_id = 'Unit is required';
        }

        if (!formData.month_year) {
            errors.month_year = 'Month-Year is required';
        } else if (!/^\d{4}-\d{2}$/.test(formData.month_year)) {
            errors.month_year = 'Format must be YYYY-MM';
        }

        if (!formData.from_date) {
            errors.from_date = 'From date is required';
        }

        if (!formData.to_date) {
            errors.to_date = 'To date is required';
        } else if (formData.from_date && formData.to_date) {
            const fromDate = new Date(formData.from_date);
            const toDate = new Date(formData.to_date);
            if (toDate < fromDate) {
                errors.to_date = 'To date cannot be before from date';
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/add_payroll.php`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchPayrolls();
                setShowAddModal(false);
                alert('Payroll period added successfully');
            } else {
                setError(response.data.error || 'Failed to add payroll period');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to add payroll period');
            console.error('Error adding payroll period:', error);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/update_payroll.php`,
                {
                    payroll_id: selectedPayroll.payroll_id,
                    ...formData
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchPayrolls();
                setShowEditModal(false);
                alert('Payroll period updated successfully');
            } else {
                setError(response.data.error || 'Failed to update payroll period');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update payroll period');
            console.error('Error updating payroll period:', error);
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/delete_payroll.php`,
                { payroll_id: selectedPayroll.payroll_id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchPayrolls();
                setShowDeleteModal(false);
                alert('Payroll period deleted successfully');
            } else {
                setError(response.data.error || 'Failed to delete payroll period');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete payroll period');
            console.error('Error deleting payroll period:', error);
        }
    };

    const handleProcessConfirm = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const userId = localStorage.getItem('userId');

            const response = await axios.post(`${API_BASE}/process_payroll.php`,
                {
                    payroll_id: selectedPayroll.payroll_id,
                    processed_by: userId
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchPayrolls();
                setShowProcessModal(false);
                alert('Payroll processed successfully');
            } else {
                setError(response.data.error || 'Failed to process payroll');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to process payroll');
            console.error('Error processing payroll:', error);
        }
    };

    const getStatusBadge = (status) => {
        const badgeClasses = {
            'Open': 'bg-warning',
            'Processed': 'bg-success',
            'Closed': 'bg-danger'
        };
        return badgeClasses[status] || 'bg-secondary';
    };

    const filteredPayrolls = payrolls.filter(payroll => {
        const company = companies.find(c => c.company_id === payroll.company_id);
        const unit = units.find(u => u.unit_id === payroll.unit_id);

        return (
            payroll.month_year?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            payroll.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            company?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            unit?.unit_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    const sortedPayrolls = [...filteredPayrolls].sort((a, b) => {
        switch (sortBy) {
            case 'month_year':
                return b.month_year.localeCompare(a.month_year);
            case 'status':
                return a.status.localeCompare(b.status);
            case 'from_date':
                return new Date(b.from_date) - new Date(a.from_date);
            default:
                return b.month_year.localeCompare(a.month_year);
        }
    });

    // Generate year options for filter
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

    // Generate month options for filter
    const monthOptions = [
        { value: '', label: 'All Months' },
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    // Generate status options for filter
    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'Open', label: 'Open' },
        { value: 'Processed', label: 'Processed' },
        { value: 'Closed', label: 'Closed' }
    ];

    // Show loading while permissions are being fetched
    if (loading || !userPermissions.loaded) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading permissions...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // If user doesn't have view permission, show access denied
    if (!canViewPayrolls()) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                                <h3 className="mt-3 text-warning">Access Denied</h3>
                                <p className="text-muted">You do not have permission to view payroll periods.</p>
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    <i className="bi bi-speedometer2 me-2"></i>
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <PayrollHeader
                navigate={navigate}
                canAddPayroll={canAddPayroll}
                canEditPayroll={canEditPayroll}
                canDeletePayroll={canDeletePayroll}
                handleAdd={handleAdd}
            />

            {/* Quick Navigation */}
            <QuickNavigation navigate={navigate} />

            {/* Error Alert */}
            {error && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>
                                <strong>Error:</strong> {error}
                                <div className="mt-2">
                                    <button
                                        className="btn btn-sm btn-outline-danger me-2"
                                        onClick={fetchPayrolls}
                                    >
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Retry
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setError('')}
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Search and Filter Bar */}
            <SearchFilterBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                yearFilter={yearFilter}
                setYearFilter={setYearFilter}
                monthFilter={monthFilter}
                setMonthFilter={setMonthFilter}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                yearOptions={yearOptions}
                monthOptions={monthOptions}
                statusOptions={statusOptions}
                fetchPayrolls={fetchPayrolls}
                canExportPayrolls={canExportPayrolls}
                canAddPayroll={canAddPayroll}
                handleAdd={handleAdd}
            />

            {/* Payroll Periods List */}
            <PayrollList
                sortedPayrolls={sortedPayrolls}
                payrolls={payrolls}
                companies={companies}
                units={units}
                employees={employees}
                sortBy={sortBy}
                setSortBy={setSortBy}
                canAddPayroll={canAddPayroll}
                canEditPayroll={canEditPayroll}
                canProcessPayroll={canProcessPayroll}
                canDeletePayroll={canDeletePayroll}
                handleAdd={handleAdd}
                handleEdit={handleEdit}
                handleProcess={handleProcess}
                handleDelete={handleDelete}
                getStatusBadge={getStatusBadge}
            />

            {/* Stats Footer */}
            <PayrollStats payrolls={payrolls} />

            {/* Quick Actions Footer */}
            <QuickActions
                fetchPayrolls={fetchPayrolls}
                navigate={navigate}
                canAddPayroll={canAddPayroll}
                handleAdd={handleAdd}
            />

            {/* Modals */}
            <AddPayrollModal
                showAddModal={showAddModal}
                setShowAddModal={setShowAddModal}
                formData={formData}
                validationErrors={validationErrors}
                units={units}
                unitsLoading={unitsLoading}
                unitsError={unitsError}
                handleChange={handleChange}
                handleAddSubmit={handleAddSubmit}
                fetchUnits={fetchUnits}
            />

            <EditPayrollModal
                showEditModal={showEditModal}
                setShowEditModal={setShowEditModal}
                formData={formData}
                validationErrors={validationErrors}
                units={units}
                unitsLoading={unitsLoading}
                unitsError={unitsError}
                handleChange={handleChange}
                handleEditSubmit={handleEditSubmit}
                fetchUnits={fetchUnits}
            />

            <ProcessPayrollModal
                showProcessModal={showProcessModal}
                setShowProcessModal={setShowProcessModal}
                selectedPayroll={selectedPayroll}
                handleProcessConfirm={handleProcessConfirm}
                getStatusBadge={getStatusBadge}
            />

            <DeletePayrollModal
                showDeleteModal={showDeleteModal}
                setShowDeleteModal={setShowDeleteModal}
                selectedPayroll={selectedPayroll}
                handleDeleteConfirm={handleDeleteConfirm}
                getStatusBadge={getStatusBadge}
            />
        </div>
    );
};

export default PayrollMain;